/**
 * WEB-1510 — язык публичной страницы.
 *
 * Сервер умеет отдавать переведённое содержимое (`?lang=` у публичных ручек
 * квиза и экрана цен), но публичная страница НИКОГДА его об этом не просила:
 * весь оплаченный механизм переводов работал вхолостую. Здесь живёт единый
 * ответ на вопрос «на каком языке показываем посетителю».
 *
 * Порядок приоритетов (п.1 карточки, вариант по умолчанию — согласование с
 * владельцем продукта зафиксировано комментарием к карточке):
 *   1. `?lang=` в адресе — явное намерение того, кто дал ссылку;
 *   2. ранее запомненный выбор в этой вкладке (sessionStorage) — чтобы язык
 *      не слетал на следующем шаге воронки и при возврате с оплаты;
 *   3. языки браузера (`navigator.languages`) — ВЕСЬ список по порядку
 *      (WEB-2471 п.2), сервер берёт первый, на который есть перевод;
 *   4. null — «язык не запрашиваем», сервер отдаёт исходный язык воронки.
 *
 * Возвращается ТОЛЬКО код языка; никакого видимого переключателя на публичной
 * странице эта карточка не рисует (п.5 — отдельная карточка со своим макетом).
 */

/** Ключ хранится в рамках вкладки: выбор живёт ровно один визит. */
const STORAGE_KEY = 'w2a_lang';

/**
 * Зеркало серверного sanitizeLanguageCode
 * (backend/src/shared/redis/public-content-cache.util.ts) и
 * `normaliseVisitorLanguage` пограничного воркера
 * (infra/cloudflare/w2a-funnel-router.js): 2-3 буквы + необязательный второй
 * субтег. Сервер сравнивает язык перевода регистрозависимо (WEB-1390), а
 * переводы хранятся каноническими кодами (pt-BR, zh-CN, zh-TW, es-419) —
 * поэтому код приводится к КАНОНИЧЕСКОМУ регистру BCP-47 (WEB-2471 п.1):
 * язык строчными, регион из двух букв заглавными (`pt-br` → `pt-BR`),
 * письменность из четырёх букв с заглавной (`zh-hant` → `zh-Hant`), прочее
 * (цифровой регион `es-419`) строчными, то есть как есть. Раньше регион
 * оставался как прислали, и ссылка `?lang=pt-br` бразильский перевод не
 * находила. Совпадение трёх мест держит сторож
 * backend/src/shared/guards/visitor-language-ends.web2471.spec.ts.
 *
 * Проверка нужна и на клиенте: мусорный `?lang=` не должен ни улетать в
 * запрос, ни оседать в хранилище (шаг 8 сценария приёмки — `?lang=zz`
 * открывает страницу на исходном языке без ошибки; `zz` формально валиден,
 * перевода для него просто нет, и сервер честно отдаёт оригинал).
 */
export function sanitizeVisitorLanguage(
  raw: string | null | undefined,
): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 12) {
    return null;
  }
  if (!/^[a-z]{2,3}(-[a-z0-9]{2,8})?$/i.test(raw)) return null;
  const [primary, subtag] = raw.split('-');
  if (subtag === undefined) return primary.toLowerCase();
  return `${primary.toLowerCase()}-${canonicalSubtag(subtag)}`;
}

/** Регистр второго субтега по BCP-47: регион ЗАГЛАВНЫМИ, письменность С Заглавной. */
function canonicalSubtag(subtag: string): string {
  if (/^[a-z]{2}$/i.test(subtag)) return subtag.toUpperCase();
  if (/^[a-z]{4}$/i.test(subtag)) {
    return subtag[0].toUpperCase() + subtag.slice(1).toLowerCase();
  }
  return subtag.toLowerCase();
}

/**
 * WEB-2471 п.2 — потолок длины списка языков браузера. Тот же потолок у
 * сервера (`MAX_VISITOR_LANGUAGES`, backend/src/shared/utils/language-lookup.util.ts)
 * и у воркера: каждый язык списка — до двух поисков перевода на сервере и
 * часть ключа копии на краю. Пять с запасом покрывают живые браузеры
 * («de-DE, de, en-US, en»).
 */
export const MAX_VISITOR_LANGUAGES = 5;

function readStored(): string | null {
  try {
    return sanitizeVisitorLanguage(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    // Приватный режим/запрет хранилища — не повод падать публичной странице.
    return null;
  }
}

function remember(lang: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // см. readStored
  }
}

/**
 * Все годные языки браузера по порядку предпочтения, без дублей, не больше
 * MAX_VISITOR_LANGUAGES.
 *
 * WEB-2471 п.2: раньше брался только ПЕРВЫЙ годный язык. Посетитель с
 * браузером «немецкий, английский» у воронки с купленным английским и без
 * немецкого видел исходный язык — второй язык в списке почти всегда
 * английский. Теперь на сервер уходит весь список (`langs`), сервер отдаёт
 * первый язык списка, на который есть видимый посетителю перевод.
 */
function browserLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];
  // `navigator.language` — первый элемент того же списка; отдельно он нужен
  // только старым браузерам, где списка нет вовсе. Добавлять его в конец
  // непустого списка нельзя: он ВСЕГДА повторяет уже учтённый язык.
  const languages = Array.isArray(navigator.languages)
    ? navigator.languages
    : [];
  const candidates = languages.length > 0 ? languages : [navigator.language];
  const result: string[] = [];
  for (const candidate of candidates) {
    // Ревизия пачки 5 (Н-4): код уходит ПОЛНЫМ (pt-BR, а не pt) — переводы
    // владелец заказывает региональными кодами, а откат на основной субтег
    // при промахе делает сервер (backend/src/shared/utils/language-lookup.util.ts):
    // правило одно и в одном месте.
    const sanitized = sanitizeVisitorLanguage(candidate);
    if (sanitized && !result.includes(sanitized)) result.push(sanitized);
    if (result.length >= MAX_VISITOR_LANGUAGES) break;
  }
  return result;
}

/**
 * Лестница языка посетителя целиком (WEB-1510, решение В6-21):
 * `?lang` → запомненный выбор вкладки → языки браузера → пусто.
 *
 * Явный `?lang` и запомненный выбор — ОДИН язык: это намерение того, кто дал
 * ссылку, подменять его следующим языком браузера нельзя. Список из
 * нескольких языков бывает только из браузера, и в хранилище вкладки он не
 * пишется (запоминание — только из `?lang`, как было).
 *
 * @param explicitLang значение `?lang=` из адреса, если оно там есть.
 * @returns коды языков по порядку предпочтения; пусто — «не передавать».
 */
export function resolveVisitorLanguages(
  explicitLang?: string | null,
): string[] {
  const explicit = sanitizeVisitorLanguage(explicitLang);
  if (explicit) {
    remember(explicit);
    return [explicit];
  }
  const stored = readStored();
  if (stored) return [stored];
  return browserLanguages();
}

/**
 * @param explicitLang значение `?lang=` из адреса (react-router
 *        `searchParams.get('lang')`), если оно там есть.
 * @returns первый язык лестницы либо null — «не передавать». Нужен там, где
 *        язык один по смыслу: пакет надписей до загрузки воронки, системные
 *        экраны. Публичные запросы содержимого шлют весь список
 *        (`visitorLanguageQuery`).
 */
export function resolveVisitorLanguage(
  explicitLang?: string | null,
): string | null {
  return resolveVisitorLanguages(explicitLang)[0] ?? null;
}

/**
 * Хвост адреса публичного запроса содержимого (без `?`/`&` впереди).
 *
 *   []            → ""                    — язык не передаём;
 *   ["pt-BR"]     → "lang=pt-BR"          — ровно как раньше;
 *   ["de", "en"]  → "lang=de&langs=de,en".
 *
 * `lang` с первым языком остаётся и при списке: сервер без поддержки `langs`
 * (порядок выката любой) отвечает так же, как сегодня. Сервер, знающий
 * `langs`, перебирает весь список (WEB-2471 п.2). Ключ копии на краю
 * строится по обоим параметрам (infra/cloudflare/w2a-funnel-router.js).
 */
export function visitorLanguageQuery(languages: readonly string[]): string {
  if (languages.length === 0) return '';
  const lang = `lang=${encodeURIComponent(languages[0])}`;
  if (languages.length === 1) return lang;
  return `${lang}&langs=${languages.map(encodeURIComponent).join(',')}`;
}
